import { useState, useCallback } from 'react';
import personaService from '../services/personaService';
import i18n from '../i18n/config';
import { Persona } from '../types'; // Assurez-vous que ce type est correct

// Type pour la création de persona
type PersonaCreatePayload = {
  name: string;
  description?: string | null;
  role?: string | null;
  citation?: string | null;
  objectif?: string | null;
  frustration?: string | null;
};

// Type pour la mise à jour (Partial de Create)
type PersonaUpdatePayload = Partial<PersonaCreatePayload>;

interface UsePersonasCRUDResult {
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean; // Ajouter état pour suppression
  error: string | null;
  createPersona: (data: PersonaCreatePayload) => Promise<Persona | null>;
  updatePersona: (personaId: string, data: PersonaUpdatePayload) => Promise<Persona | null>;
  deletePersona: (personaId: string) => Promise<boolean>; // Ajouter la fonction delete
}

export const usePersonasCRUD = (): UsePersonasCRUDResult => {
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // Nouvel état
  const [error, setError] = useState<string | null>(null);

  const handleApiError = (err: any, context: string, fallbackKey: string): string => {
    console.error(`Error ${context}:`, err);
    const errorMsg = err.response?.data?.detail || err.message || i18n.t(fallbackKey);
    setError(errorMsg);
    return errorMsg;
  };

  const createPersona = useCallback(async (data: PersonaCreatePayload): Promise<Persona | null> => {
    setIsCreating(true);
    setError(null);
    try {
      const persona = await personaService.create(data);
      return persona;
    } catch (err) {
      handleApiError(err, 'creating persona', 'errors.persona.createFailed');
      return null;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const updatePersona = useCallback(async (personaId: string, data: PersonaUpdatePayload): Promise<Persona | null> => {
    setIsUpdating(true);
    setError(null);
    try {
      // Filtrer les clés undefined car le backend attend null ou une valeur
      const payload = Object.fromEntries(Object.entries(data).filter(([, v]) => v !== undefined));
      const persona = await personaService.update(personaId, payload);
      return persona;
    } catch (err) {
      handleApiError(err, 'updating persona', 'errors.persona.updateFailed');
      return null;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const deletePersona = useCallback(async (personaId: string): Promise<boolean> => {
    setIsDeleting(true);
    setError(null);
    try {
      await personaService.delete(personaId);
      return true; // Succès
    } catch (err) {
      handleApiError(err, 'deleting persona', 'errors.persona.deleteFailed');
      return false; // Échec
    } finally {
      setIsDeleting(false);
    }
  }, []);


  return {
    isCreating,
    isUpdating,
    isDeleting, // Retourner le nouvel état
    error,
    createPersona,
    updatePersona,
    deletePersona, // Retourner la nouvelle fonction
  };
};
